import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { toast } from "sonner";
import { LogOut, UserPlus, Upload, Users, FileText, Check, X, Trash2, Edit, Settings, TrendingUp, Plus, Home, FileSignature, FolderOpen, Download, Search, ChevronsUpDown } from "lucide-react";
import ContractEditor from "@/components/ContractEditor";

const AdminDashboard = ({ user, onLogout }) => {
  const [clients, setClients] = useState([]);
  const [files, setFiles] = useState([]);
  const [settings, setSettings] = useState(null);
  const [showAddClient, setShowAddClient] = useState(false);
  const [showEditClient, setShowEditClient] = useState(false);
  const [showUploadFile, setShowUploadFile] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Search/Filter states
  const [clientSearch, setClientSearch] = useState("");
  const [fileSearch, setFileSearch] = useState("");
  const [uploadClientComboOpen, setUploadClientComboOpen] = useState(false);

  // Add client form
  const [newClient, setNewClient] = useState({
    username: "",
    email: "",
    password: "",
    company_type: "LTD",
    has_vat: false,
    apartments: "",
    contract_template_id: null
  });

  // Edit client form
  const [editClient, setEditClient] = useState(null);

  // Upload file form - default to previous month
  const getPreviousMonth = () => {
    const now = new Date();
    const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return { year: prevMonth.getFullYear(), month: prevMonth.getMonth() + 1 };
  };
  
  const [fileUpload, setFileUpload] = useState({
    client_id: "",
    apartment_name: "",
    amount_eur: "",
    client_net_income: "",
    year: getPreviousMonth().year,
    month: getPreviousMonth().month,
    file: null,
    takk_pdf: null,
    invoice_pdf: null,
    admin_message: ""
  });

  const [editPaymentDetails, setEditPaymentDetails] = useState(null);
  
  // Apartment management
  const [showAddApartment, setShowAddApartment] = useState(false);
  const [selectedClientForApartment, setSelectedClientForApartment] = useState(null);
  const [newApartmentName, setNewApartmentName] = useState("");
  
  // Files tab filter - use same getPreviousMonth function as fileUpload
  const prevMonth = getPreviousMonth();
  const [filesFilterYear, setFilesFilterYear] = useState(prevMonth.year);
  const [filesFilterMonth, setFilesFilterMonth] = useState(prevMonth.month.toString());
  
  // Income tab
  const [incomeYear, setIncomeYear] = useState(new Date().getFullYear());
  const [incomeMonth, setIncomeMonth] = useState("0");
  const [incomeSummary, setIncomeSummary] = useState(null);
  const [activeTab, setActiveTab] = useState("clients");
  
  // Edit/Delete file
  const [showEditFile, setShowEditFile] = useState(false);
  const [showDeleteFile, setShowDeleteFile] = useState(false);
  const [editingFile, setEditingFile] = useState(null);
  const [deletingFile, setDeletingFile] = useState(null);
  
  // Additional files
  const [additionalFiles, setAdditionalFiles] = useState([]);
  
  // Admin management
  const [admins, setAdmins] = useState([]);
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [showEditAdmin, setShowEditAdmin] = useState(false);
  const [showDeleteAdmin, setShowDeleteAdmin] = useState(false);
  const [newAdmin, setNewAdmin] = useState({
    username: "",
    email: "",
    password: ""
  });
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [deletingAdmin, setDeletingAdmin] = useState(null);
  
  // SMTP Configuration
  const [smtpConfig, setSmtpConfig] = useState({
    host: "",
    port: 587,
    username: "",
    password: "",
    use_tls: true,
    from_email: ""
  });
  const [testingSMTP, setTestingSMTP] = useState(false);
  const [savingSMTP, setSavingSMTP] = useState(false);
  
  // Contract Templates
  const [contractTemplates, setContractTemplates] = useState([]);
  const [showAddTemplate, setShowAddTemplate] = useState(false);
  const [showEditTemplate, setShowEditTemplate] = useState(false);
  const [showDeleteTemplate, setShowDeleteTemplate] = useState(false);
  const [newTemplate, setNewTemplate] = useState({
    name: "",
    content: "",
    fields: [],
    is_active: true
  });
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [deletingTemplate, setDeletingTemplate] = useState(null);
  
  // Form Configuration
  const [formConfig, setFormConfig] = useState({ fields: [] });
  const [savingFormConfig, setSavingFormConfig] = useState(false);
  const [editingFieldOptions, setEditingFieldOptions] = useState(null);
  const [showOptionsDialog, setShowOptionsDialog] = useState(false);
  
  // Saved Contracts
  const [savedContracts, setSavedContracts] = useState([]);
  const [contractsFilter, setContractsFilter] = useState("");
  const [showEditContract, setShowEditContract] = useState(false);
  const [showDeleteContract, setShowDeleteContract] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [deletingContract, setDeletingContract] = useState(null);

  // Supportive Documents
  const [supportiveTopics, setSupportiveTopics] = useState([]);
  const [showAddTopic, setShowAddTopic] = useState(false);
  const [showEditTopic, setShowEditTopic] = useState(false);
  const [showDeleteTopic, setShowDeleteTopic] = useState(false);
  const [showUploadFiles, setShowUploadFiles] = useState(false);
  const [currentTopic, setCurrentTopic] = useState(null);
  const [newTopic, setNewTopic] = useState({
    title: "",
    description: "",
    is_active: true
  });
  const [uploadingFiles, setUploadingFiles] = useState([]);

  useEffect(() => {
    fetchClients();
    fetchFiles();
    fetchSettings();
    fetchAdmins();
    fetchSMTPConfig();
    fetchContractTemplates();
    fetchFormConfig();
    fetchSavedContracts();
    fetchSupportiveTopics();
  }, []);
  
  // Handle Files tab first-time open with previous month default
  useEffect(() => {
    if (activeTab === "files") {
      const hasOpenedFiles = sessionStorage.getItem('files_tab_opened');
      if (!hasOpenedFiles) {
        // Already defaults to previous month in state initialization
        sessionStorage.setItem('files_tab_opened', 'true');
      }
    }
  }, [activeTab]);
  
  // Handle Income tab first-time open with previous month default
  useEffect(() => {
    if (activeTab === "income") {
      const hasOpenedIncome = sessionStorage.getItem('income_tab_opened');
      if (!hasOpenedIncome) {
        // Set to previous month
        const now = new Date();
        const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        setIncomeYear(prevMonth.getFullYear());
        setIncomeMonth((prevMonth.getMonth() + 1).toString());
        sessionStorage.setItem('income_tab_opened', 'true');
        // Auto-fetch with previous month
        setTimeout(() => fetchIncome(), 100);
      }
    }
  }, [activeTab]);
  
  // Filter files based on year/month
  const getFilteredFiles = () => {
    return files.filter(file => {
      const yearMatch = filesFilterYear === "0" || file.year === parseInt(filesFilterYear);
      const monthMatch = filesFilterMonth === "0" || file.month === parseInt(filesFilterMonth);
      
      // Search filter
      let searchMatch = true;
      if (fileSearch) {
        const search = fileSearch.toLowerCase();
        searchMatch = (
          file.client_email?.toLowerCase().includes(search) ||
          file.apartment_name?.toLowerCase().includes(search) ||
          file.filename?.toLowerCase().includes(search)
        );
      }
      
      return yearMatch && monthMatch && searchMatch;
    });
  };

  const getToken = () => localStorage.getItem('token');

  const fetchClients = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/clients`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setClients(data);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchFiles = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/files`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setFiles(data);
      }
    } catch (error) {
      console.error('Error fetching files:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/settings`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };
  
  // Admin Management Functions
  const fetchAdmins = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/admins`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setAdmins(data);
      }
    } catch (error) {
      console.error('Error fetching admins:', error);
    }
  };
  
  const handleAddAdmin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/admins`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newAdmin)
      });
      
      if (response.ok) {
        toast.success('Admin added successfully!');
        setShowAddAdmin(false);
        setNewAdmin({ username: "", email: "", password: "" });
        fetchAdmins();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add admin');
      }
    } catch (error) {
      console.error('Error adding admin:', error);
      toast.error('Failed to add admin');
    } finally {
      setLoading(false);
    }
  };
  
  const handleEditAdmin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/admins/${editingAdmin.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: editingAdmin.username,
          email: editingAdmin.email,
          ...(editingAdmin.password && { password: editingAdmin.password })
        })
      });
      
      if (response.ok) {
        toast.success('Admin updated successfully!');
        setShowEditAdmin(false);
        setEditingAdmin(null);
        fetchAdmins();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update admin');
      }
    } catch (error) {
      console.error('Error updating admin:', error);
      toast.error('Failed to update admin');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteAdmin = async () => {
    setLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/admins/${deletingAdmin.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });
      
      if (response.ok) {
        toast.success('Admin deleted successfully!');
        setShowDeleteAdmin(false);
        setDeletingAdmin(null);
        fetchAdmins();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete admin');
      }
    } catch (error) {
      console.error('Error deleting admin:', error);
      toast.error('Failed to delete admin');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchSMTPConfig = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/smtp/config`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setSmtpConfig(data);
      } else if (response.status !== 404) {
        // Only show error if not "not found" (which is expected for first-time setup)
        console.error('Error fetching SMTP config');
      }
    } catch (error) {
      console.error('Error fetching SMTP config:', error);
    }
  };
  
  const handleSaveSMTPConfig = async () => {
    setSavingSMTP(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/smtp/config`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(smtpConfig)
      });
      
      if (response.ok) {
        toast.success('SMTP configuration saved successfully!');
        fetchSMTPConfig(); // Refresh to get masked password
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save SMTP configuration');
      }
    } catch (error) {
      console.error('Error saving SMTP config:', error);
      toast.error('Failed to save SMTP configuration');
    } finally {
      setSavingSMTP(false);
    }
  };
  
  const handleTestSMTP = async () => {
    setTestingSMTP(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/smtp/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(smtpConfig)
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'SMTP connection successful!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'SMTP connection failed');
      }
    } catch (error) {
      console.error('Error testing SMTP:', error);
      toast.error('Failed to test SMTP connection');
    } finally {
      setTestingSMTP(false);
    }
  };
  
  const fetchContractTemplates = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/contract-templates`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setContractTemplates(data);
      }
    } catch (error) {
      console.error('Error fetching contract templates:', error);
    }
  };
  
  const handleAddTemplate = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/contract-templates`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newTemplate)
      });
      
      if (response.ok) {
        toast.success('Contract template created successfully!');
        setShowAddTemplate(false);
        setNewTemplate({ name: "", content: "", fields: [], is_active: true });
        fetchContractTemplates();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create template');
      }
    } catch (error) {
      console.error('Error creating template:', error);
      toast.error('Failed to create template');
    } finally {
      setLoading(false);
    }
  };
  
  const handleEditTemplate = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/contract-templates/${editingTemplate.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: editingTemplate.name,
          content: editingTemplate.content,
          is_active: editingTemplate.is_active
        })
      });
      
      if (response.ok) {
        toast.success('Contract template updated successfully!');
        setShowEditTemplate(false);
        setEditingTemplate(null);
        fetchContractTemplates();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update template');
      }
    } catch (error) {
      console.error('Error updating template:', error);
      toast.error('Failed to update template');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteTemplate = async () => {
    setLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/contract-templates/${deletingTemplate.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });
      
      if (response.ok) {
        toast.success('Contract template deleted successfully!');
        setShowDeleteTemplate(false);
        setDeletingTemplate(null);
        fetchContractTemplates();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete template');
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchFormConfig = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/form-config`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setFormConfig(data);
      }
    } catch (error) {
      console.error('Error fetching form config:', error);
    }
  };
  
  const handleSaveFormConfig = async () => {
    setSavingFormConfig(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/form-config`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ fields: formConfig.fields })
      });
      
      if (response.ok) {
        toast.success('Form configuration saved successfully!');
        fetchFormConfig();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save form configuration');
      }
    } catch (error) {
      console.error('Error saving form config:', error);
      toast.error('Failed to save form configuration');
    } finally {
      setSavingFormConfig(false);
    }
  };
  
  // handleFieldToggle function removed - all fields are now always enabled
  
  const handleFieldRequiredToggle = (fieldId) => {
    const updatedFields = formConfig.fields.map(field => 
      field.field_id === fieldId ? { ...field, required: !field.required } : field
    );
    setFormConfig({ ...formConfig, fields: updatedFields });
  };
  
  const handleFieldLabelChange = (fieldId, newLabel) => {
    const updatedFields = formConfig.fields.map(field => 
      field.field_id === fieldId ? { ...field, label: newLabel } : field
    );
    setFormConfig({ ...formConfig, fields: updatedFields });
  };
  
  const fetchSavedContracts = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/contracts`, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setSavedContracts(data);
      }
    } catch (error) {
      console.error('Error fetching saved contracts:', error);
    }
  };
  
  const getContractStatus = (endDate) => {
    const now = new Date();
    const end = new Date(endDate);
    const daysUntilExpiry = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) {
      return { label: 'Expired', color: 'bg-red-100 text-red-800' };
    } else if (daysUntilExpiry <= 30) {
      return { label: 'Expiring Soon', color: 'bg-orange-100 text-orange-800' };
    } else {
      return { label: 'Active', color: 'bg-green-100 text-green-800' };
    }
  };
  
  const handleDeleteContract = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/contracts/${deletingContract.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });
      
      if (response.ok) {
        toast.success('Contract deleted successfully!');
        setShowDeleteContract(false);
        setDeletingContract(null);
        fetchSavedContracts();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete contract');
      }
    } catch (error) {
      console.error('Error deleting contract:', error);
      toast.error('Failed to delete contract');
    } finally {
      setLoading(false);
    }
  };

  const fetchIncome = async () => {
    try {
      let url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/income?year=${incomeYear}`;
      if (incomeMonth && incomeMonth !== "0") {
        url += `&month=${incomeMonth}`;
      }
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setIncomeSummary(data);
      }
    } catch (error) {
      console.error('Error fetching income:', error);
    }
  };

  // ============================================
  // Supportive Documents Functions
  // ============================================
  
  const fetchSupportiveTopics = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/supportive-documents/topics`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSupportiveTopics(data);
      }
    } catch (error) {
      console.error('Error fetching supportive topics:', error);
    }
  };
  
  const handleCreateTopic = async () => {
    if (!newTopic.title.trim() || !newTopic.description.trim()) {
      toast.error('Please enter title and description');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/supportive-documents/topics`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newTopic)
      });
      
      if (response.ok) {
        toast.success('Topic created successfully!');
        setShowAddTopic(false);
        setNewTopic({ title: "", description: "", is_active: true });
        fetchSupportiveTopics();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create topic');
      }
    } catch (error) {
      console.error('Error creating topic:', error);
      toast.error('Failed to create topic');
    } finally {
      setLoading(false);
    }
  };
  
  const handleUpdateTopic = async () => {
    if (!currentTopic) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/supportive-documents/topics/${currentTopic.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: currentTopic.title,
          description: currentTopic.description,
          is_active: currentTopic.is_active
        })
      });
      
      if (response.ok) {
        toast.success('Topic updated successfully!');
        setShowEditTopic(false);
        setCurrentTopic(null);
        fetchSupportiveTopics();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update topic');
      }
    } catch (error) {
      console.error('Error updating topic:', error);
      toast.error('Failed to update topic');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteTopic = async () => {
    if (!currentTopic) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/supportive-documents/topics/${currentTopic.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (response.ok) {
        toast.success('Topic deleted successfully!');
        setShowDeleteTopic(false);
        setCurrentTopic(null);
        fetchSupportiveTopics();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete topic');
      }
    } catch (error) {
      console.error('Error deleting topic:', error);
      toast.error('Failed to delete topic');
    } finally {
      setLoading(false);
    }
  };
  
  const handleUploadFiles = async () => {
    if (!currentTopic || uploadingFiles.length === 0) {
      toast.error('Please select files to upload');
      return;
    }
    
    // Check file size (25MB max)
    const maxSize = 25 * 1024 * 1024;
    for (const file of uploadingFiles) {
      if (file.size > maxSize) {
        toast.error(`File ${file.name} exceeds 25MB limit`);
        return;
      }
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      uploadingFiles.forEach(file => {
        formData.append('files', file);
      });
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/supportive-documents/topics/${currentTopic.id}/files`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` },
        body: formData
      });
      
      if (response.ok) {
        toast.success('Files uploaded successfully!');
        setShowUploadFiles(false);
        setUploadingFiles([]);
        fetchSupportiveTopics();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to upload files');
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error('Failed to upload files');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteSupportiveFile = async (fileId) => {
    if (!confirm('Are you sure you want to delete this file?')) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/supportive-documents/files/${fileId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (response.ok) {
        toast.success('File deleted successfully!');
        fetchSupportiveTopics();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete file');
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error('Failed to delete file');
    }
  };
  
  const getFileIcon = (fileType) => {
    const icons = {
      'pdf': '📄',
      'doc': '📝',
      'docx': '📝',
      'xls': '📊',
      'xlsx': '📊',
      'zip': '🗜️',
      'jpg': '🖼️',
      'jpeg': '🖼️',
      'png': '🖼️',
    };
    return icons[fileType?.toLowerCase()] || '📁';
  };

  const handleAddApartment = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/clients/${selectedClientForApartment.id}/apartments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({ apartment_name: newApartmentName })
      });

      if (response.ok) {
        toast.success('Apartment added successfully!');
        setShowAddApartment(false);
        setNewApartmentName("");
        fetchClients();
      } else {
        toast.error('Failed to add apartment');
      }
    } catch (error) {
      console.error('Error adding apartment:', error);
      toast.error('Failed to add apartment');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveApartment = async (clientId, apartmentName) => {
    if (!window.confirm(`Remove apartment "${apartmentName}"?`)) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/clients/${clientId}/apartments/${encodeURIComponent(apartmentName)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });

      if (response.ok) {
        toast.success('Apartment removed');
        fetchClients();
      } else {
        toast.error('Failed to remove apartment');
      }
    } catch (error) {
      console.error('Error removing apartment:', error);
      toast.error('Failed to remove apartment');
    }
  };

  const handleUpdateSettings = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/settings`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        toast.success('Settings updated successfully!');
        fetchSettings();
      } else {
        toast.error('Failed to update settings');
      }
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error('Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  const handleAddClient = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const clientData = { ...newClient, role: 'client' };
      // Convert comma-separated apartments to array
      if (newClient.apartments) {
        clientData.apartments = newClient.apartments.split(',').map(a => a.trim()).filter(a => a);
      }
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/clients`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify(clientData)
      });

      if (response.ok) {
        toast.success('Client added successfully!');
        setShowAddClient(false);
        setNewClient({ username: "", email: "", password: "", company_type: "LTD", has_vat: false, apartments: "", contract_template_id: null });
        fetchClients();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add client');
      }
    } catch (error) {
      console.error('Error adding client:', error);
      toast.error('Failed to add client');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClient = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const updateData = {
        email: editClient.email,
        company_type: editClient.company_type,
        has_vat: editClient.has_vat || false,
        contract_template_id: editClient.contract_template_id || null,
        first_name: editClient.first_name || '',
        last_name: editClient.last_name || '',
        phone: editClient.phone || ''
      };

      // Only include password if it's been changed
      if (editClient.newPassword && editClient.newPassword.trim() !== '') {
        updateData.password = editClient.newPassword;
      }
      
      // Convert comma-separated apartments to array
      updateData.apartments = editClient.apartments ? editClient.apartments.split(',').map(a => a.trim()).filter(a => a) : [];

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/clients/${editClient.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        toast.success('Client updated successfully!');
        setShowEditClient(false);
        setEditClient(null);
        fetchClients();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update client');
      }
    } catch (error) {
      console.error('Error updating client:', error);
      toast.error('Failed to update client');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClient = async (clientId, clientUsername) => {
    if (!window.confirm(`Are you sure you want to delete client "${clientUsername}"? All their files will be deleted.`)) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/clients/${clientId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });

      if (response.ok) {
        toast.success('Client deleted successfully');
        fetchClients();
        fetchFiles();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete client');
      }
    } catch (error) {
      console.error('Error deleting client:', error);
      toast.error('Failed to delete client');
    }
  };

  const handleUploadFile = async (e) => {
    e.preventDefault();
    if (!fileUpload.file) {
      toast.error('Please select a file');
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('file', fileUpload.file);
    formData.append('client_id', fileUpload.client_id);
    formData.append('year', fileUpload.year);
    formData.append('month', fileUpload.month);
    
    if (fileUpload.apartment_name) {
      formData.append('apartment_name', fileUpload.apartment_name);
    }
    if (fileUpload.amount_eur) {
      formData.append('amount_eur', fileUpload.amount_eur);
    }
    if (fileUpload.client_net_income) {
      formData.append('client_net_income', fileUpload.client_net_income);
    }
    if (fileUpload.takk_pdf) {
      formData.append('takk_pdf', fileUpload.takk_pdf);
    }
    if (fileUpload.invoice_pdf) {
      formData.append('invoice_pdf', fileUpload.invoice_pdf);
    }
    if (fileUpload.admin_message) {
      formData.append('admin_message', fileUpload.admin_message);
    }
    
    // Append additional files (max 5)
    if (additionalFiles && additionalFiles.length > 0) {
      additionalFiles.forEach((file) => {
        formData.append('additional_files', file);
      });
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`
        },
        body: formData
      });

      if (response.ok) {
        toast.success('File uploaded successfully!');
        setShowUploadFile(false);
        const prevMonth = getPreviousMonth();
        setFileUpload({
          client_id: "",
          apartment_name: "",
          amount_eur: "",
          client_net_income: "",
          year: prevMonth.year,
          month: prevMonth.month,
          file: null,
          takk_pdf: null,
          invoice_pdf: null,
          admin_message: ""
        });
        setAdditionalFiles([]);
        fetchFiles();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to upload file');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
    } finally {
      setLoading(false);
    }
  };

  const togglePaymentStatus = async (fileId, currentStatus) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/files/${fileId}/payment`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({ payment_status: !currentStatus })
      });

      if (response.ok) {
        toast.success(`Payment marked as ${!currentStatus ? 'paid' : 'unpaid'}`);
        fetchFiles();
      } else {
        toast.error('Failed to update payment status');
      }
    } catch (error) {
      console.error('Error updating payment:', error);
      toast.error('Failed to update payment status');
    }
  };

  const handleUpdatePaymentDetails = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/files/${editPaymentDetails.id}/details`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          stripe_link: editPaymentDetails.stripe_link,
          iban_details: editPaymentDetails.iban_details
        })
      });

      if (response.ok) {
        toast.success('Payment details updated');
        setEditPaymentDetails(null);
        fetchFiles();
      } else {
        toast.error('Failed to update payment details');
      }
    } catch (error) {
      console.error('Error updating payment details:', error);
      toast.error('Failed to update payment details');
    }
  };

  const handleEditFile = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const formData = new FormData();
    
    // Add text fields if changed
    if (editingFile.client_id) formData.append('client_id', editingFile.client_id);
    if (editingFile.year) formData.append('year', editingFile.year);
    if (editingFile.month) formData.append('month', editingFile.month);
    if (editingFile.apartment_name !== undefined) formData.append('apartment_name', editingFile.apartment_name || '');
    if (editingFile.amount_eur !== undefined) formData.append('amount_eur', editingFile.amount_eur || '');
    if (editingFile.client_net_income !== undefined) formData.append('client_net_income', editingFile.client_net_income || '');
    if (editingFile.admin_message !== undefined) formData.append('admin_message', editingFile.admin_message || '');
    
    // Add file replacements if provided
    if (editingFile.newFile) formData.append('file', editingFile.newFile);
    if (editingFile.newTakkPdf) formData.append('takk_pdf', editingFile.newTakkPdf);
    if (editingFile.newInvoicePdf) formData.append('invoice_pdf', editingFile.newInvoicePdf);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/files/${editingFile.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${getToken()}`
        },
        body: formData
      });
      
      if (response.ok) {
        toast.success('File record updated successfully!');
        setShowEditFile(false);
        setEditingFile(null);
        fetchFiles();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update file record');
      }
    } catch (error) {
      console.error('Error updating file:', error);
      toast.error('Failed to update file record');
    } finally {
      setLoading(false);
    }
  };
  
  const handleDeleteFile = async () => {
    setLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/files/${deletingFile.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getToken()}`
        }
      });
      
      if (response.ok) {
        toast.success('File record deleted successfully!');
        setShowDeleteFile(false);
        setDeletingFile(null);
        fetchFiles();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete file record');
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error('Failed to delete file record');
    } finally {
      setLoading(false);
    }
  };

  const getMonthName = (month) => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return months[month - 1];
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_owner-portal-3/artifacts/9p342eak_deltakey-logo-full%20%284%29.png" 
                alt="Delta Key" 
                className="h-8 sm:h-10"
              />
              <div className="hidden sm:block">
                <p className="text-sm text-gray-500">Admin Dashboard</p>
              </div>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-gray-900">{user.username}</p>
                <p className="text-xs text-gray-500">Administrator</p>
              </div>
              <Button
                data-testid="admin-logout-button"
                onClick={onLogout}
                variant="outline"
                className="border-gray-300 hover:bg-gray-100"
                size="sm"
              >
                <LogOut className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="clients" value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            <TabsList className="bg-white border border-gray-200 p-1 inline-flex min-w-full sm:min-w-0 w-auto">
              <TabsTrigger value="clients" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white whitespace-nowrap text-sm">
                <Users className="w-4 h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Clients</span>
                <span className="sm:hidden">Clients</span>
              </TabsTrigger>
              <TabsTrigger value="files" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white whitespace-nowrap text-sm">
                <FileText className="w-4 h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Files & Payments</span>
                <span className="sm:hidden">Files</span>
              </TabsTrigger>
              <TabsTrigger value="income" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white whitespace-nowrap text-sm">
                <TrendingUp className="w-4 h-4 mr-1 sm:mr-2" />
                <span>Income</span>
              </TabsTrigger>
              <TabsTrigger value="admins" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white whitespace-nowrap text-sm">
                <UserPlus className="w-4 h-4 mr-1 sm:mr-2" />
                <span>Admins</span>
              </TabsTrigger>
              <TabsTrigger value="contracts" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white whitespace-nowrap text-sm">
                <FileSignature className="w-4 h-4 mr-1 sm:mr-2" />
                <span>Contracts</span>
              </TabsTrigger>
              <TabsTrigger value="supportive-docs" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white whitespace-nowrap text-sm">
                <FolderOpen className="w-4 h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Supportive Docs</span>
                <span className="sm:hidden">Docs</span>
              </TabsTrigger>
              <TabsTrigger value="settings" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white whitespace-nowrap text-sm">
                <Settings className="w-4 h-4 mr-1 sm:mr-2" />
                <span>Settings</span>
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Clients Tab */}
          <TabsContent value="clients" className="space-y-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Client Management</h2>
              <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                <div className="relative flex-1 sm:flex-initial sm:w-64">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    type="text"
                    placeholder="Search by name or apartment..."
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Dialog open={showAddClient} onOpenChange={setShowAddClient}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-client-button" className="bg-blue-600 hover:bg-blue-700 text-white">
                      <UserPlus className="w-4 h-4 mr-2" />
                      Add Client
                    </Button>
                  </DialogTrigger>
                <DialogContent className="max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Add New Client</DialogTitle>
                    <DialogDescription>
                      Create a new client account with login credentials
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleAddClient} className="space-y-4 pb-4">
                    <div className="space-y-2">
                      <Label htmlFor="username">Username</Label>
                      <Input
                        id="username"
                        data-testid="new-client-username"
                        value={newClient.username}
                        onChange={(e) => setNewClient({ ...newClient, username: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        data-testid="new-client-email"
                        type="email"
                        value={newClient.email}
                        onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="password">Password</Label>
                      <Input
                        id="password"
                        data-testid="new-client-password"
                        type="password"
                        value={newClient.password}
                        onChange={(e) => setNewClient({ ...newClient, password: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company_type">Company Type</Label>
                      <Select
                        value={newClient.company_type}
                        onValueChange={(value) => setNewClient({ ...newClient, company_type: value })}
                      >
                        <SelectTrigger data-testid="new-client-company-type">
                          <SelectValue placeholder="Select company type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="LTD">LTD</SelectItem>
                          <SelectItem value="LP">LP</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {newClient.company_type === 'LP' && (
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="has_vat"
                          data-testid="new-client-has-vat"
                          checked={newClient.has_vat}
                          onChange={(e) => setNewClient({ ...newClient, has_vat: e.target.checked })}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <Label htmlFor="has_vat" className="cursor-pointer">
                          This LP client requires VAT invoices
                        </Label>
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label htmlFor="new-contract-template">Assign Contract Template</Label>
                      <Select
                        value={newClient.contract_template_id || 'none'}
                        onValueChange={(value) => setNewClient({ ...newClient, contract_template_id: value === 'none' ? null : value })}
                      >
                        <SelectTrigger id="new-contract-template">
                          <SelectValue placeholder="Select a contract template" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No template assigned</SelectItem>
                          {contractTemplates.filter(t => t.is_active).map((template) => (
                            <SelectItem key={template.id} value={template.id}>
                              {template.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500">Client will see this contract template in their dashboard</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="apartments">Apartment Names (comma separated)</Label>
                      <Input
                        id="apartments"
                        data-testid="new-client-apartments"
                        placeholder="e.g., Apartment A, Villa Blue, Studio 101"
                        value={newClient.apartments || ''}
                        onChange={(e) => setNewClient({ ...newClient, apartments: e.target.value })}
                      />
                      <p className="text-xs text-gray-500">Optional: Enter apartment names separated by commas</p>
                    </div>
                    <Button data-testid="submit-new-client" type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={loading}>
                      {loading ? 'Adding...' : 'Add Client'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Edit Client Dialog */}
            <Dialog open={showEditClient} onOpenChange={setShowEditClient}>
              <DialogContent className="max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Edit Client</DialogTitle>
                  <DialogDescription>
                    Update client information
                  </DialogDescription>
                </DialogHeader>
                {editClient && (
                  <form onSubmit={handleEditClient} className="space-y-4 pb-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit-username">Username</Label>
                      <Input
                        id="edit-username"
                        value={editClient.username}
                        disabled
                        className="bg-gray-100"
                      />
                      <p className="text-xs text-gray-500">Username cannot be changed</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-email">Email</Label>
                      <Input
                        id="edit-email"
                        data-testid="edit-client-email"
                        type="email"
                        value={editClient.email}
                        onChange={(e) => setEditClient({ ...editClient, email: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-password">New Password (leave empty to keep current)</Label>
                      <Input
                        id="edit-password"
                        data-testid="edit-client-password"
                        type="password"
                        placeholder="Enter new password or leave blank"
                        value={editClient.newPassword}
                        onChange={(e) => setEditClient({ ...editClient, newPassword: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-company_type">Company Type</Label>
                      <Select
                        value={editClient.company_type}
                        onValueChange={(value) => setEditClient({ ...editClient, company_type: value })}
                      >
                        <SelectTrigger data-testid="edit-client-company-type">
                          <SelectValue placeholder="Select company type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="LTD">LTD</SelectItem>
                          <SelectItem value="LP">LP</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {editClient.company_type === 'LP' && (
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="edit-has_vat"
                          data-testid="edit-client-has-vat"
                          checked={editClient.has_vat || false}
                          onChange={(e) => setEditClient({ ...editClient, has_vat: e.target.checked })}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <Label htmlFor="edit-has_vat" className="cursor-pointer">
                          This LP client requires VAT invoices
                        </Label>
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label htmlFor="edit-apartments">Apartment Names (comma separated)</Label>
                      <Input
                        id="edit-apartments"
                        data-testid="edit-client-apartments"
                        placeholder="e.g., Apartment A, Villa Blue, Studio 101"
                        value={editClient.apartments || ''}
                        onChange={(e) => setEditClient({ ...editClient, apartments: e.target.value })}
                      />
                      <p className="text-xs text-gray-500">Comma-separated list of apartments</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-contract-template">Assigned Contract Template</Label>
                      <Select
                        value={editClient.contract_template_id || 'none'}
                        onValueChange={(value) => setEditClient({ ...editClient, contract_template_id: value === 'none' ? null : value })}
                      >
                        <SelectTrigger id="edit-contract-template">
                          <SelectValue placeholder="Select a contract template" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No template assigned</SelectItem>
                          {contractTemplates.filter(t => t.is_active).map((template) => (
                            <SelectItem key={template.id} value={template.id}>
                              {template.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500">Client will see this contract template in their dashboard</p>
                    </div>
                    <Button data-testid="submit-edit-client" type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={loading}>
                      {loading ? 'Updating...' : 'Update Client'}
                    </Button>
                  </form>
                )}
              </DialogContent>
            </Dialog>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {clients.filter((client) => {
                if (!clientSearch) return true;
                const search = clientSearch.toLowerCase();
                return (
                  client.username?.toLowerCase().includes(search) ||
                  client.email?.toLowerCase().includes(search) ||
                  client.first_name?.toLowerCase().includes(search) ||
                  client.last_name?.toLowerCase().includes(search) ||
                  client.apartments?.some(apt => apt.toLowerCase().includes(search))
                );
              }).map((client) => (
                <Card key={client.id} className="bg-white border-gray-200 hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-lg">{client.username}</CardTitle>
                        <CardDescription>{client.email}</CardDescription>
                        <p className="text-xs text-gray-500 mt-1">Company: {client.company_type || 'LTD'}</p>
                      </div>
                      <div className="flex space-x-1">
                        <Button
                          data-testid={`edit-client-${client.username}`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setEditClient({
                              id: client.id,
                              username: client.username,
                              email: client.email,
                              company_type: client.company_type || 'LTD',
                              has_vat: client.has_vat || false,
                              apartments: client.apartments ? client.apartments.join(', ') : '',
                              contract_template_id: client.contract_template_id || null,
                              first_name: client.first_name || '',
                              last_name: client.last_name || '',
                              phone: client.phone || '',
                              newPassword: ''
                            });
                            setShowEditClient(true);
                          }}
                          size="sm"
                          className="bg-gray-100 text-gray-700 active:bg-gray-200 touch-manipulation"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          data-testid={`delete-client-${client.username}`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDeleteClient(client.id, client.username);
                          }}
                          size="sm"
                          className="bg-red-100 text-red-600 active:bg-red-200 touch-manipulation"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-500">
                      Files: {files.filter(f => f.client_id === client.id).length}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {clients.length === 0 && (
              <Card className="bg-white border-gray-200">
                <CardContent className="text-center py-12">
                  <Users className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600">No clients yet. Add your first client to get started.</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Files Tab */}
          <TabsContent value="files" className="space-y-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">File Management</h2>
              <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                <div className="relative flex-1 sm:flex-initial sm:w-64">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    type="text"
                    placeholder="Search by client or apartment..."
                    value={fileSearch}
                    onChange={(e) => setFileSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Dialog open={showUploadFile} onOpenChange={(open) => {
                  setShowUploadFile(open);
                  if (!open) setUploadClientComboOpen(false); // Close combobox when dialog closes
                }}>
                  <DialogTrigger asChild>
                    <Button data-testid="upload-file-button" className="bg-blue-600 hover:bg-blue-700 text-white">
                      <Upload className="w-4 h-4 mr-2" />
                      Upload File
                    </Button>
                  </DialogTrigger>
                <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Upload Monthly File</DialogTitle>
                    <DialogDescription>
                      Upload a monthly reservation file for a client (payment info auto-filled from settings)
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleUploadFile} className="space-y-4 pb-4">
                    <div className="space-y-2">
                      <Label htmlFor="client">Client</Label>
                      <Popover open={uploadClientComboOpen} onOpenChange={setUploadClientComboOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={uploadClientComboOpen}
                            className="w-full justify-between"
                            type="button"
                          >
                            {fileUpload.client_id
                              ? (() => {
                                  const selectedClient = clients.find(c => c.id === fileUpload.client_id);
                                  return selectedClient 
                                    ? `${selectedClient.username} (${selectedClient.email})${selectedClient.apartments?.length > 0 ? ` - ${selectedClient.apartments.join(', ')}` : ''}`
                                    : "Select a client";
                                })()
                              : "Select a client"}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[500px] p-0">
                          <Command>
                            <CommandInput placeholder="Search by name or apartment..." />
                            <CommandList>
                              <CommandEmpty>No client found.</CommandEmpty>
                              <CommandGroup>
                                {clients.map((client) => (
                                  <CommandItem
                                    key={client.id}
                                    value={`${client.username} ${client.email} ${client.first_name || ''} ${client.last_name || ''} ${client.apartments?.join(' ') || ''}`}
                                    onSelect={() => {
                                      setFileUpload({ ...fileUpload, client_id: client.id });
                                      setUploadClientComboOpen(false);
                                    }}
                                  >
                                    <Check
                                      className={`mr-2 h-4 w-4 ${
                                        fileUpload.client_id === client.id ? "opacity-100" : "opacity-0"
                                      }`}
                                    />
                                    <div className="flex flex-col">
                                      <span className="font-medium">{client.username}</span>
                                      <span className="text-sm text-gray-500">
                                        {client.email}
                                        {client.apartments?.length > 0 && ` • ${client.apartments.join(', ')}`}
                                        {` • ${client.company_type || 'LTD'}`}
                                      </span>
                                    </div>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="year">Year</Label>
                        <Input
                          id="year"
                          data-testid="upload-year"
                          type="number"
                          value={fileUpload.year}
                          onChange={(e) => setFileUpload({ ...fileUpload, year: parseInt(e.target.value) })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="month">Month</Label>
                        <Input
                          id="month"
                          data-testid="upload-month"
                          type="number"
                          min="1"
                          max="12"
                          value={fileUpload.month}
                          onChange={(e) => setFileUpload({ ...fileUpload, month: parseInt(e.target.value) })}
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="apartment_name">Apartment Name (Optional)</Label>
                      <Input
                        id="apartment_name"
                        data-testid="upload-apartment-name"
                        placeholder="Select or enter apartment name"
                        value={fileUpload.apartment_name}
                        onChange={(e) => setFileUpload({ ...fileUpload, apartment_name: e.target.value })}
                        list="apartment-suggestions"
                      />
                      {fileUpload.client_id && clients.find(c => c.id === fileUpload.client_id)?.apartments?.length > 0 && (
                        <datalist id="apartment-suggestions">
                          {clients.find(c => c.id === fileUpload.client_id).apartments.map((apt, idx) => (
                            <option key={idx} value={apt} />
                          ))}
                        </datalist>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="amount_eur">Delta Key Fee (EUR)</Label>
                      <Input
                        id="amount_eur"
                        data-testid="upload-amount-eur"
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={fileUpload.amount_eur}
                        onChange={(e) => setFileUpload({ ...fileUpload, amount_eur: e.target.value })}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="client_net_income">Client Net Income (EUR)</Label>
                      <Input
                        id="client_net_income"
                        data-testid="upload-client-net-income"
                        type="number"
                        step="0.01"
                        placeholder="0.00"
                        value={fileUpload.client_net_income}
                        onChange={(e) => setFileUpload({ ...fileUpload, client_net_income: e.target.value })}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="file">Monthly Report File (XLS/XLSX)</Label>
                      <Input
                        id="file"
                        data-testid="upload-file-input"
                        type="file"
                        accept=".xls,.xlsx"
                        onChange={(e) => setFileUpload({ ...fileUpload, file: e.target.files[0] })}
                        required
                      />
                    </div>
                    
                    {/* LTD-specific fields */}
                    {clients.find(c => c.id === fileUpload.client_id)?.company_type === 'LTD' && (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="takk_pdf">TAKK PDF (Optional)</Label>
                          <Input
                            id="takk_pdf"
                            data-testid="upload-takk-pdf"
                            type="file"
                            accept=".pdf"
                            onChange={(e) => setFileUpload({ ...fileUpload, takk_pdf: e.target.files[0] })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="invoice_pdf">Invoice PDF (Optional)</Label>
                          <Input
                            id="invoice_pdf"
                            data-testid="upload-invoice-pdf"
                            type="file"
                            accept=".pdf"
                            onChange={(e) => setFileUpload({ ...fileUpload, invoice_pdf: e.target.files[0] })}
                          />
                        </div>
                      </>
                    )}
                    
                    {/* LP + VAT client Invoice field */}
                    {clients.find(c => c.id === fileUpload.client_id)?.company_type === 'LP' && 
                     clients.find(c => c.id === fileUpload.client_id)?.has_vat && (
                      <div className="space-y-2">
                        <Label htmlFor="invoice_pdf_lp">Invoice PDF (Optional)</Label>
                        <Input
                          id="invoice_pdf_lp"
                          data-testid="upload-invoice-pdf-lp"
                          type="file"
                          accept=".pdf"
                          onChange={(e) => setFileUpload({ ...fileUpload, invoice_pdf: e.target.files[0] })}
                        />
                        <p className="text-xs text-gray-500">For LP clients with VAT</p>
                      </div>
                    )}
                    
                    <div className="space-y-2">
                      <Label htmlFor="additional_files">Other Documents (Optional - Max 5 files)</Label>
                      <Input
                        id="additional_files"
                        type="file"
                        multiple
                        accept="*/*"
                        onChange={(e) => {
                          const files = Array.from(e.target.files).slice(0, 5);
                          setAdditionalFiles(files);
                        }}
                      />
                      <p className="text-xs text-gray-500">Upload up to 5 additional documents (any file type: pdf, jpg, png, txt, doc, xlsx, etc.)</p>
                      {additionalFiles.length > 0 && (
                        <div className="text-xs text-gray-600 mt-1">
                          Selected: {additionalFiles.map(f => f.name).join(', ')}
                        </div>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="admin_message">Important Message for Client (Optional)</Label>
                      <Input
                        id="admin_message"
                        data-testid="upload-admin-message"
                        placeholder="Enter important notification for the client"
                        value={fileUpload.admin_message}
                        onChange={(e) => setFileUpload({ ...fileUpload, admin_message: e.target.value })}
                      />
                    </div>
                    
                    <Button data-testid="submit-upload-file" type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={loading}>
                      {loading ? 'Uploading...' : 'Upload File'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            </div>

            {/* Filter Card */}
            <Card className="bg-white border-gray-200">
              <CardHeader>
                <CardTitle>Filter Files</CardTitle>
                <CardDescription>Filter by year and/or month (defaults to previous month on first login)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="files-filter-year">Year</Label>
                    <Select
                      value={filesFilterYear.toString()}
                      onValueChange={(value) => setFilesFilterYear(value === "0" ? "0" : parseInt(value))}
                    >
                      <SelectTrigger id="files-filter-year">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">All Years</SelectItem>
                        {[2025, 2024, 2023, 2022, 2021].map(year => (
                          <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="files-filter-month">Month</Label>
                    <Select
                      value={filesFilterMonth}
                      onValueChange={(value) => setFilesFilterMonth(value)}
                    >
                      <SelectTrigger id="files-filter-month">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">All Months</SelectItem>
                        <SelectItem value="1">January</SelectItem>
                        <SelectItem value="2">February</SelectItem>
                        <SelectItem value="3">March</SelectItem>
                        <SelectItem value="4">April</SelectItem>
                        <SelectItem value="5">May</SelectItem>
                        <SelectItem value="6">June</SelectItem>
                        <SelectItem value="7">July</SelectItem>
                        <SelectItem value="8">August</SelectItem>
                        <SelectItem value="9">September</SelectItem>
                        <SelectItem value="10">October</SelectItem>
                        <SelectItem value="11">November</SelectItem>
                        <SelectItem value="12">December</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <p className="text-sm text-gray-600">
                  Showing {getFilteredFiles().length} of {files.length} files
                </p>
              </CardContent>
            </Card>

            <div className="space-y-4">
              {getFilteredFiles().map((file) => (
                <Card key={file.id} className="bg-white border-gray-200 hover:shadow-lg transition-shadow animate-slide-up">
                  <CardContent className="pt-6">
                    <div className="flex flex-col lg:flex-row items-start justify-between gap-4">
                      <div className="flex-1 w-full">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <FileText className="w-5 h-5 text-gray-600" />
                          <h3 className="font-semibold text-base sm:text-lg text-gray-900">
                            {getMonthName(file.month)} {file.year}
                          </h3>
                          <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium ${
                            file.payment_status
                              ? 'bg-green-100 text-green-700'
                              : 'bg-amber-100 text-amber-700'
                          }`}>
                            {file.payment_status ? 'Paid' : 'Pending'}
                          </span>
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                            {file.company_type}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-1">Client: {file.client_email}</p>
                        <p className="text-sm text-gray-600 mb-1">File: {file.filename}</p>
                        
                        {/* Log: Last create/edit */}
                        <p className="text-xs text-gray-500 italic mb-3">
                          {file.last_modified_at 
                            ? `Last edited: ${new Date(file.last_modified_at).toLocaleString()} by ${file.last_modified_by || 'Unknown'}`
                            : `Created: ${new Date(file.uploaded_at).toLocaleString()} by ${file.uploaded_by_username || 'Unknown'}`
                          }
                        </p>
                        
                        <div className="flex items-center space-x-2 mb-3">
                          <a
                            href={`${process.env.REACT_APP_BACKEND_URL}${file.file_url}`}
                            download
                            className="text-sm text-gray-800 hover:text-black underline"
                            data-testid={`download-file-${file.id}`}
                          >
                            Download File
                          </a>
                        </div>

                        {(file.stripe_link || file.iban_details || file.revolut_tag || file.wise_account) && (
                          <div className="bg-gray-50 p-3 rounded-lg text-sm space-y-1">
                            {file.stripe_link && (
                              <p className="text-gray-700">
                                <span className="font-medium">Stripe:</span>{' '}
                                <a href={file.stripe_link} target="_blank" rel="noopener noreferrer" className="text-gray-800 hover:underline">
                                  Payment Link
                                </a>
                              </p>
                            )}
                            {file.iban_details && (
                              <p className="text-gray-700">
                                <span className="font-medium">IBAN:</span> {file.iban_details}
                              </p>
                            )}
                            {file.revolut_tag && (
                              <p className="text-gray-700">
                                <span className="font-medium">Revolut:</span> {file.revolut_tag}
                              </p>
                            )}
                            {file.wise_account && (
                              <p className="text-gray-700">
                                <span className="font-medium">Wise:</span> {file.wise_account}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex flex-col space-y-2 w-full lg:w-auto lg:min-w-[180px]">
                        <Button
                          data-testid={`toggle-payment-${file.id}`}
                          onClick={() => togglePaymentStatus(file.id, file.payment_status)}
                          variant={file.payment_status ? "outline" : "default"}
                          size="sm"
                          className={`w-full ${file.payment_status
                            ? "border-red-300 text-red-600 hover:bg-red-50"
                            : "bg-green-600 hover:bg-green-700 text-white"
                          }`}
                        >
                          {file.payment_status ? (
                            <><X className="w-4 h-4 mr-2" /> Mark Unpaid</>
                          ) : (
                            <><Check className="w-4 h-4 mr-2" /> Mark Paid</>
                          )}
                        </Button>
                        <Button
                          onClick={() => {
                            setEditingFile(file);
                            setShowEditFile(true);
                          }}
                          variant="outline"
                          size="sm"
                          className="w-full border-blue-300 text-blue-600 hover:bg-blue-50"
                        >
                          <Edit className="w-4 h-4 mr-2" /> Edit
                        </Button>
                        <Button
                          onClick={() => {
                            setDeletingFile(file);
                            setShowDeleteFile(true);
                          }}
                          variant="outline"
                          size="sm"
                          className="w-full border-gray-300 text-gray-600 hover:bg-gray-50"
                        >
                          <Trash2 className="w-4 h-4 mr-2" /> Delete
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {getFilteredFiles().length === 0 && files.length > 0 && (
              <Card className="bg-white border-gray-200">
                <CardContent className="text-center py-12">
                  <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600">No files match the selected filters. Try adjusting your year or month selection.</p>
                </CardContent>
              </Card>
            )}
            
            {files.length === 0 && (
              <Card className="bg-white border-gray-200">
                <CardContent className="text-center py-12">
                  <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600">No files uploaded yet. Upload your first file to get started.</p>
                </CardContent>
              </Card>
            )}
            
            {/* Edit File Dialog */}
            <Dialog open={showEditFile} onOpenChange={setShowEditFile}>
              <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Edit File Record</DialogTitle>
                  <DialogDescription>
                    Update file information and optionally replace files
                  </DialogDescription>
                </DialogHeader>
                {editingFile && (
                  <form onSubmit={handleEditFile} className="space-y-4 pb-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit-client">Client</Label>
                      <Select
                        value={editingFile.client_id}
                        onValueChange={(value) => setEditingFile({ ...editingFile, client_id: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {clients.map((client) => (
                            <SelectItem key={client.id} value={client.id}>
                              {client.username} ({client.email}) - {client.company_type || 'LTD'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="edit-year">Year</Label>
                        <Input
                          id="edit-year"
                          type="number"
                          value={editingFile.year}
                          onChange={(e) => setEditingFile({ ...editingFile, year: parseInt(e.target.value) })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit-month">Month</Label>
                        <Input
                          id="edit-month"
                          type="number"
                          min="1"
                          max="12"
                          value={editingFile.month}
                          onChange={(e) => setEditingFile({ ...editingFile, month: parseInt(e.target.value) })}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-apartment-name">Apartment Name</Label>
                      <Input
                        id="edit-apartment-name"
                        value={editingFile.apartment_name || ''}
                        onChange={(e) => setEditingFile({ ...editingFile, apartment_name: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-amount">Delta Key Fee (EUR)</Label>
                      <Input
                        id="edit-amount"
                        type="number"
                        step="0.01"
                        value={editingFile.amount_eur || ''}
                        onChange={(e) => setEditingFile({ ...editingFile, amount_eur: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-client-net-income">Client Net Income (EUR)</Label>
                      <Input
                        id="edit-client-net-income"
                        type="number"
                        step="0.01"
                        value={editingFile.client_net_income || ''}
                        onChange={(e) => setEditingFile({ ...editingFile, client_net_income: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-message">Admin Message</Label>
                      <Input
                        id="edit-message"
                        value={editingFile.admin_message || ''}
                        onChange={(e) => setEditingFile({ ...editingFile, admin_message: e.target.value })}
                      />
                    </div>
                    
                    <div className="border-t pt-4">
                      <h4 className="font-medium mb-2">Replace Files (Optional)</h4>
                      <div className="space-y-3">
                        <div className="space-y-2">
                          <Label htmlFor="edit-main-file">Main File</Label>
                          <Input
                            id="edit-main-file"
                            type="file"
                            accept=".xls,.xlsx"
                            onChange={(e) => setEditingFile({ ...editingFile, newFile: e.target.files[0] })}
                          />
                          <p className="text-xs text-gray-500">Current: {editingFile.filename}</p>
                        </div>
                        {editingFile.takk_pdf_filename && (
                          <div className="space-y-2">
                            <Label htmlFor="edit-takk">TAKK PDF</Label>
                            <Input
                              id="edit-takk"
                              type="file"
                              accept=".pdf"
                              onChange={(e) => setEditingFile({ ...editingFile, newTakkPdf: e.target.files[0] })}
                            />
                            <p className="text-xs text-gray-500">Current: {editingFile.takk_pdf_filename}</p>
                          </div>
                        )}
                        {editingFile.invoice_pdf_filename && (
                          <div className="space-y-2">
                            <Label htmlFor="edit-invoice">Invoice PDF</Label>
                            <Input
                              id="edit-invoice"
                              type="file"
                              accept=".pdf"
                              onChange={(e) => setEditingFile({ ...editingFile, newInvoicePdf: e.target.files[0] })}
                            />
                            <p className="text-xs text-gray-500">Current: {editingFile.invoice_pdf_filename}</p>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex space-x-2">
                      <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700" disabled={loading}>
                        {loading ? 'Updating...' : 'Update File'}
                      </Button>
                      <Button type="button" variant="outline" onClick={() => setShowEditFile(false)}>
                        Cancel
                      </Button>
                    </div>
                  </form>
                )}
              </DialogContent>
            </Dialog>
            
            {/* Delete File Dialog */}
            <Dialog open={showDeleteFile} onOpenChange={setShowDeleteFile}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Delete File Record</DialogTitle>
                  <DialogDescription>
                    Are you sure you want to delete this file record? This will permanently delete the file and all associated data.
                  </DialogDescription>
                </DialogHeader>
                {deletingFile && (
                  <div className="space-y-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-700"><strong>Client:</strong> {deletingFile.client_email}</p>
                      <p className="text-sm text-gray-700"><strong>Period:</strong> {getMonthName(deletingFile.month)} {deletingFile.year}</p>
                      <p className="text-sm text-gray-700"><strong>File:</strong> {deletingFile.filename}</p>
                    </div>
                    <div className="flex space-x-2">
                      <Button 
                        onClick={handleDeleteFile} 
                        className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                        disabled={loading}
                      >
                        {loading ? 'Deleting...' : 'Delete'}
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => {
                          setShowDeleteFile(false);
                          setDeletingFile(null);
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Admins Tab */}
          <TabsContent value="admins" className="space-y-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Admin Accounts</h2>
                <p className="text-sm text-gray-600">Manage administrator accounts</p>
              </div>
              <Button 
                onClick={() => setShowAddAdmin(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Add Admin
              </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {admins.map((admin) => (
                <Card key={admin.id} className="bg-white border-gray-200">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-gray-900">{admin.username}</CardTitle>
                        <CardDescription>{admin.email}</CardDescription>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditingAdmin({ ...admin, password: "" });
                            setShowEditAdmin(true);
                          }}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setDeletingAdmin(admin);
                            setShowDeleteAdmin(true);
                          }}
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-sm text-gray-600">
                      <p>Role: Administrator</p>
                      <p>Created: {new Date(admin.created_at).toLocaleDateString()}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Add Admin Dialog */}
            <Dialog open={showAddAdmin} onOpenChange={setShowAddAdmin}>
              <DialogContent className="max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Add New Admin</DialogTitle>
                  <DialogDescription>Create a new administrator account</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleAddAdmin} className="space-y-4">
                  <div>
                    <Label htmlFor="admin-username">Username</Label>
                    <Input
                      id="admin-username"
                      value={newAdmin.username}
                      onChange={(e) => setNewAdmin({...newAdmin, username: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="admin-email">Email</Label>
                    <Input
                      id="admin-email"
                      type="email"
                      value={newAdmin.email}
                      onChange={(e) => setNewAdmin({...newAdmin, email: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="admin-password">Password</Label>
                    <Input
                      id="admin-password"
                      type="password"
                      value={newAdmin.password}
                      onChange={(e) => setNewAdmin({...newAdmin, password: e.target.value})}
                      required
                    />
                  </div>
                  <div className="flex justify-end gap-3">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setShowAddAdmin(false)}
                    >
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      disabled={loading}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      {loading ? 'Adding...' : 'Add Admin'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>

            {/* Edit Admin Dialog */}
            <Dialog open={showEditAdmin} onOpenChange={setShowEditAdmin}>
              <DialogContent className="max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Edit Admin</DialogTitle>
                  <DialogDescription>Update administrator details</DialogDescription>
                </DialogHeader>
                {editingAdmin && (
                  <form onSubmit={handleEditAdmin} className="space-y-4">
                    <div>
                      <Label htmlFor="edit-admin-username">Username</Label>
                      <Input
                        id="edit-admin-username"
                        value={editingAdmin.username}
                        onChange={(e) => setEditingAdmin({...editingAdmin, username: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-admin-email">Email</Label>
                      <Input
                        id="edit-admin-email"
                        type="email"
                        value={editingAdmin.email}
                        onChange={(e) => setEditingAdmin({...editingAdmin, email: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-admin-password">New Password (leave blank to keep current)</Label>
                      <Input
                        id="edit-admin-password"
                        type="password"
                        value={editingAdmin.password || ""}
                        onChange={(e) => setEditingAdmin({...editingAdmin, password: e.target.value})}
                        placeholder="Leave blank to keep current password"
                      />
                    </div>
                    <div className="flex justify-end gap-3">
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => setShowEditAdmin(false)}
                      >
                        Cancel
                      </Button>
                      <Button 
                        type="submit" 
                        disabled={loading}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        {loading ? 'Updating...' : 'Update Admin'}
                      </Button>
                    </div>
                  </form>
                )}
              </DialogContent>
            </Dialog>

            {/* Delete Admin Dialog */}
            <Dialog open={showDeleteAdmin} onOpenChange={setShowDeleteAdmin}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Delete Admin</DialogTitle>
                  <DialogDescription>
                    Are you sure you want to delete this admin account? This action cannot be undone.
                  </DialogDescription>
                </DialogHeader>
                {deletingAdmin && (
                  <div className="space-y-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="font-medium">{deletingAdmin.username}</p>
                      <p className="text-sm text-gray-600">{deletingAdmin.email}</p>
                    </div>
                    <div className="flex justify-end gap-3">
                      <Button 
                        variant="outline" 
                        onClick={() => setShowDeleteAdmin(false)}
                      >
                        Cancel
                      </Button>
                      <Button 
                        onClick={handleDeleteAdmin}
                        disabled={loading}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        {loading ? 'Deleting...' : 'Delete Admin'}
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Contracts Tab */}
          <TabsContent value="contracts" className="space-y-4">
            <Tabs defaultValue="templates" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="templates">Templates</TabsTrigger>
                <TabsTrigger value="saved">Saved Contracts</TabsTrigger>
              </TabsList>
              
              {/* Templates Sub-Tab */}
              <TabsContent value="templates" className="space-y-4 mt-4">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Contract Templates</h2>
              <Dialog open={showAddTemplate} onOpenChange={setShowAddTemplate}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Template
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Add New Contract Template</DialogTitle>
                    <DialogDescription>
                      Create a contract template with placeholders for client data
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleAddTemplate} className="space-y-4 pb-4">
                    <div className="space-y-2">
                      <Label htmlFor="template-name">Template Name</Label>
                      <Input
                        id="template-name"
                        placeholder="e.g., Standard Rental Agreement"
                        value={newTemplate.name}
                        onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Template Content</Label>
                      <ContractEditor
                        content={newTemplate.content}
                        onChange={(html) => setNewTemplate({ ...newTemplate, content: html })}
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="template-active"
                        checked={newTemplate.is_active}
                        onChange={(e) => setNewTemplate({ ...newTemplate, is_active: e.target.checked })}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <Label htmlFor="template-active" className="cursor-pointer">Active Template</Label>
                    </div>
                    <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={loading}>
                      {loading ? 'Creating...' : 'Create Template'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Templates List */}
            <div className="grid grid-cols-1 gap-4">
              {contractTemplates.map((template) => (
                <Card key={template.id} className="bg-white border-gray-200">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <CardTitle className="flex items-center gap-2">
                          {template.name}
                          {template.is_active && (
                            <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">Active</span>
                          )}
                          {!template.is_active && (
                            <span className="px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded">Inactive</span>
                          )}
                        </CardTitle>
                        <CardDescription>
                          Created: {new Date(template.created_at).toLocaleDateString()}
                        </CardDescription>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditingTemplate(template);
                            setShowEditTemplate(true);
                          }}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setDeletingTemplate(template);
                            setShowDeleteTemplate(true);
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div 
                      className="prose prose-sm max-w-none text-gray-700 line-clamp-3"
                      dangerouslySetInnerHTML={{ __html: template.content }}
                    />
                  </CardContent>
                </Card>
              ))}
              
              {contractTemplates.length === 0 && (
                <Card className="bg-gray-50 border-gray-200">
                  <CardContent className="text-center py-12">
                    <FileSignature className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                    <p className="text-gray-600">No contract templates yet. Create your first template!</p>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Edit Template Dialog */}
            <Dialog open={showEditTemplate} onOpenChange={setShowEditTemplate}>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Edit Contract Template</DialogTitle>
                  <DialogDescription>
                    Update the contract template content and settings
                  </DialogDescription>
                </DialogHeader>
                {editingTemplate && (
                  <form onSubmit={handleEditTemplate} className="space-y-4 pb-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit-template-name">Template Name</Label>
                      <Input
                        id="edit-template-name"
                        value={editingTemplate.name}
                        onChange={(e) => setEditingTemplate({ ...editingTemplate, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Template Content</Label>
                      <ContractEditor
                        content={editingTemplate.content}
                        onChange={(html) => setEditingTemplate({ ...editingTemplate, content: html })}
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="edit-template-active"
                        checked={editingTemplate.is_active}
                        onChange={(e) => setEditingTemplate({ ...editingTemplate, is_active: e.target.checked })}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <Label htmlFor="edit-template-active" className="cursor-pointer">Active Template</Label>
                    </div>
                    <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={loading}>
                      {loading ? 'Updating...' : 'Update Template'}
                    </Button>
                  </form>
                )}
              </DialogContent>
            </Dialog>

            {/* Delete Template Dialog */}
            <Dialog open={showDeleteTemplate} onOpenChange={setShowDeleteTemplate}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Delete Contract Template</DialogTitle>
                  <DialogDescription>
                    Are you sure you want to delete this template? This action cannot be undone.
                  </DialogDescription>
                </DialogHeader>
                {deletingTemplate && (
                  <div className="space-y-4">
                    <div className="p-4 bg-red-50 border border-red-200 rounded">
                      <p className="font-semibold text-red-900">{deletingTemplate.name}</p>
                      <p className="text-sm text-red-700 mt-1">This will permanently delete the template.</p>
                    </div>
                    <div className="flex gap-3">
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={() => setShowDeleteTemplate(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                        onClick={handleDeleteTemplate}
                        disabled={loading}
                      >
                        {loading ? 'Deleting...' : 'Delete'}
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
            
            {/* Form Builder Section */}
            <div className="mt-8">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Form Builder</h2>
                  <p className="text-sm text-gray-600 mt-1">Configure which fields appear in the client contract form</p>
                </div>
                <Button
                  onClick={handleSaveFormConfig}
                  disabled={savingFormConfig}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {savingFormConfig ? 'Saving...' : 'Save Configuration'}
                </Button>
              </div>
              
              <Card className="bg-white border-gray-200">
                <CardContent className="pt-6">
                  <div className="space-y-3">
                    {formConfig.fields && formConfig.fields.map((field) => (
                      <div
                        key={field.field_id}
                        className="p-4 border rounded-lg bg-white border-gray-300"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 space-y-3">
                            {/* Field Label */}
                            <div className="flex items-center gap-3">
                              <Input
                                value={field.label}
                                onChange={(e) => handleFieldLabelChange(field.field_id, e.target.value)}
                                className="font-medium"
                                placeholder="Field Label"
                              />
                              <span className={`px-2 py-1 text-xs rounded whitespace-nowrap ${
                                field.field_type === 'text' ? 'bg-blue-100 text-blue-800' :
                                field.field_type === 'date' ? 'bg-purple-100 text-purple-800' :
                                field.field_type === 'dropdown' ? 'bg-green-100 text-green-800' :
                                'bg-orange-100 text-orange-800'
                              }`}>
                                {field.field_type}
                              </span>
                            </div>
                            
                            {/* Required Checkbox */}
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                id={`required-${field.field_id}`}
                                checked={field.required}
                                onChange={() => handleFieldRequiredToggle(field.field_id)}
                                className="w-4 h-4 text-blue-600 rounded"
                              />
                              <Label
                                htmlFor={`required-${field.field_id}`}
                                className="text-sm text-gray-700"
                              >
                                Required field (clients must fill this)
                              </Label>
                            </div>
                            
                            {/* Dropdown Options */}
                            {(field.field_type === 'dropdown' || field.field_type === 'multiple_choice') && (
                              <div className="pl-4 border-l-2 border-blue-200">
                                <p className="text-xs text-gray-600 mb-2">Options:</p>
                                <div className="flex flex-wrap gap-2">
                                  {field.options && field.options.map((option, idx) => (
                                    <span key={idx} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded border border-blue-200">
                                      {option}
                                    </span>
                                  ))}
                                </div>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="mt-2"
                                  onClick={() => {
                                    setEditingFieldOptions(field);
                                    setShowOptionsDialog(true);
                                  }}
                                >
                                  <Edit className="w-3 h-3 mr-1" />
                                  Edit Options
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Edit Options Dialog */}
            <Dialog open={showOptionsDialog} onOpenChange={setShowOptionsDialog}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Edit {editingFieldOptions?.field_type === 'dropdown' ? 'Dropdown' : 'Multiple Choice'} Options</DialogTitle>
                  <DialogDescription>
                    Manage options for {editingFieldOptions?.label}
                  </DialogDescription>
                </DialogHeader>
                {editingFieldOptions && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Options (one per line)</Label>
                      <textarea
                        className="w-full min-h-[200px] p-3 border border-gray-300 rounded-md"
                        value={editingFieldOptions.options?.join('\n') || ''}
                        onChange={(e) => {
                          const newOptions = e.target.value.split('\n').filter(opt => opt.trim());
                          setEditingFieldOptions({ ...editingFieldOptions, options: newOptions });
                        }}
                        placeholder="Enter each option on a new line"
                      />
                    </div>
                    <div className="flex gap-3">
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={() => {
                          setShowOptionsDialog(false);
                          setEditingFieldOptions(null);
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                        onClick={() => {
                          const updatedFields = formConfig.fields.map(field =>
                            field.field_id === editingFieldOptions.field_id
                              ? { ...field, options: editingFieldOptions.options }
                              : field
                          );
                          setFormConfig({ ...formConfig, fields: updatedFields });
                          setShowOptionsDialog(false);
                          setEditingFieldOptions(null);
                          toast.success('Options updated!');
                        }}
                      >
                        Save Options
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
              </TabsContent>
              
              {/* Saved Contracts Sub-Tab */}
              <TabsContent value="saved" className="space-y-4 mt-4">
                <div className="flex flex-col sm:flex-row justify-between items-center gap-3 mb-6">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Saved Contracts</h2>
                  <Input
                    placeholder="Filter by client name..."
                    value={contractsFilter}
                    onChange={(e) => setContractsFilter(e.target.value)}
                    className="max-w-xs"
                  />
                </div>
                
                <div className="grid grid-cols-1 gap-4">
                  {savedContracts
                    .filter(contract => 
                      contractsFilter === "" || 
                      contract.client_name.toLowerCase().includes(contractsFilter.toLowerCase()) ||
                      contract.client_email.toLowerCase().includes(contractsFilter.toLowerCase())
                    )
                    .map((contract) => (
                      <Card key={contract.id} className="bg-white border-gray-200">
                        <CardHeader>
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <CardTitle className="flex items-center gap-2 flex-wrap">
                                {contract.template_name || "Contract"}
                                <span className={`px-2 py-1 text-xs rounded ${getContractStatus(contract.end_date).color}`}>
                                  {getContractStatus(contract.end_date).label}
                                </span>
                                {contract.status === 'renewed' && (
                                  <span className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-800">
                                    Renewed
                                  </span>
                                )}
                              </CardTitle>
                              <CardDescription>
                                Client: {contract.client_name} ({contract.client_email})
                              </CardDescription>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setEditingContract(contract);
                                  setShowEditContract(true);
                                }}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setDeletingContract(contract);
                                  setShowDeleteContract(true);
                                }}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Start Date:</span>
                              <span className="font-medium">{new Date(contract.start_date).toLocaleDateString()}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">End Date:</span>
                              <span className="font-medium">{new Date(contract.end_date).toLocaleDateString()}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Submitted:</span>
                              <span className="font-medium">{new Date(contract.submitted_at).toLocaleDateString()}</span>
                            </div>
                            {contract.apartment_name && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Apartment:</span>
                                <span className="font-medium">{contract.apartment_name}</span>
                              </div>
                            )}
                            <div className="flex justify-between">
                              <span className="text-gray-600">Signature Type:</span>
                              <span className="font-medium capitalize">{contract.signature_type}</span>
                            </div>
                          </div>
                          
                          {/* View Signature */}
                          {contract.signature_type === 'canvas' && contract.signature_data && (
                            <div className="mt-4 pt-4 border-t">
                              <p className="text-xs text-gray-600 mb-2">Signature:</p>
                              <img src={contract.signature_data} alt="Signature" className="h-16 border rounded" />
                            </div>
                          )}
                          {contract.signature_type === 'typed' && contract.signature_data && (
                            <div className="mt-4 pt-4 border-t">
                              <p className="text-xs text-gray-600 mb-2">Signature:</p>
                              <p className="text-2xl font-serif italic" style={{ fontFamily: "'Dancing Script', cursive" }}>
                                {contract.signature_data}
                              </p>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  
                  {savedContracts.length === 0 && (
                    <Card className="bg-gray-50 border-gray-200">
                      <CardContent className="text-center py-12">
                        <FileSignature className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                        <p className="text-gray-600">No saved contracts yet</p>
                      </CardContent>
                    </Card>
                  )}
                  
                  {savedContracts.length > 0 && savedContracts.filter(contract => 
                    contractsFilter === "" || 
                    contract.client_name.toLowerCase().includes(contractsFilter.toLowerCase()) ||
                    contract.client_email.toLowerCase().includes(contractsFilter.toLowerCase())
                  ).length === 0 && (
                    <Card className="bg-gray-50 border-gray-200">
                      <CardContent className="text-center py-12">
                        <p className="text-gray-600">No contracts match the filter "{contractsFilter}"</p>
                      </CardContent>
                    </Card>
                  )}
                </div>
                
                {/* Edit Contract Dialog */}
                <Dialog open={showEditContract} onOpenChange={setShowEditContract}>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Edit Contract</DialogTitle>
                      <DialogDescription>
                        Update contract details
                      </DialogDescription>
                    </DialogHeader>
                    {editingContract && (
                      <div className="space-y-4">
                        <div className="p-4 bg-blue-50 border border-blue-200 rounded">
                          <p className="text-sm font-medium">Contract ID: {editingContract.id}</p>
                          <p className="text-sm text-gray-600">Client: {editingContract.client_name}</p>
                        </div>
                        
                        <div className="space-y-2">
                          <Label>Start Date</Label>
                          <Input
                            type="date"
                            value={editingContract.start_date ? new Date(editingContract.start_date).toISOString().split('T')[0] : ''}
                            onChange={(e) => setEditingContract({ ...editingContract, start_date: e.target.value })}
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label>End Date</Label>
                          <Input
                            type="date"
                            value={editingContract.end_date ? new Date(editingContract.end_date).toISOString().split('T')[0] : ''}
                            onChange={(e) => setEditingContract({ ...editingContract, end_date: e.target.value })}
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label>Status</Label>
                          <Select
                            value={editingContract.status || 'active'}
                            onValueChange={(value) => setEditingContract({ ...editingContract, status: value })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="active">Active</SelectItem>
                              <SelectItem value="renewed">Renewed</SelectItem>
                              <SelectItem value="expired">Expired</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        <Button
                          onClick={async () => {
                            setLoading(true);
                            try {
                              const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/contracts/${editingContract.id}?start_date=${editingContract.start_date}&end_date=${editingContract.end_date}&status=${editingContract.status}`, {
                                method: 'PATCH',
                                headers: {
                                  'Authorization': `Bearer ${getToken()}`
                                }
                              });
                              
                              if (response.ok) {
                                toast.success('Contract updated successfully!');
                                setShowEditContract(false);
                                setEditingContract(null);
                                fetchSavedContracts();
                              } else {
                                const error = await response.json();
                                toast.error(error.detail || 'Failed to update contract');
                              }
                            } catch (error) {
                              console.error('Error updating contract:', error);
                              toast.error('Failed to update contract');
                            } finally {
                              setLoading(false);
                            }
                          }}
                          className="w-full bg-blue-600 hover:bg-blue-700"
                          disabled={loading}
                        >
                          {loading ? 'Saving...' : 'Save Changes'}
                        </Button>
                      </div>
                    )}
                  </DialogContent>
                </Dialog>
                
                {/* Delete Contract Dialog */}
                <Dialog open={showDeleteContract} onOpenChange={setShowDeleteContract}>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Delete Contract</DialogTitle>
                      <DialogDescription>
                        Are you sure you want to delete this contract? This action cannot be undone.
                      </DialogDescription>
                    </DialogHeader>
                    {deletingContract && (
                      <div className="space-y-4">
                        <div className="p-4 bg-red-50 border border-red-200 rounded">
                          <p className="font-semibold text-red-900">
                            {deletingContract.template_name || "Contract"}
                          </p>
                          <p className="text-sm text-red-700 mt-1">
                            Client: {deletingContract.client_name} ({deletingContract.client_email})
                          </p>
                          <p className="text-sm text-red-700">
                            Period: {new Date(deletingContract.start_date).toLocaleDateString()} - {new Date(deletingContract.end_date).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex gap-3">
                          <Button
                            variant="outline"
                            className="flex-1"
                            onClick={() => setShowDeleteContract(false)}
                          >
                            Cancel
                          </Button>
                          <Button
                            className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                            onClick={handleDeleteContract}
                            disabled={loading}
                          >
                            {loading ? 'Deleting...' : 'Delete Contract'}
                          </Button>
                        </div>
                      </div>
                    )}
                  </DialogContent>
                </Dialog>
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* Supportive Documents Tab */}
          <TabsContent value="supportive-docs" className="space-y-4">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Supportive Documents</h2>
              <Dialog open={showAddTopic} onOpenChange={setShowAddTopic}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Topic
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Create New Topic</DialogTitle>
                    <DialogDescription>Add a new supportive document topic</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="topic-title">Topic Title</Label>
                      <Input
                        id="topic-title"
                        value={newTopic.title}
                        onChange={(e) => setNewTopic({...newTopic, title: e.target.value})}
                        placeholder="Equipment Guidelines"
                      />
                    </div>
                    <div>
                      <Label htmlFor="topic-description">Description</Label>
                      <ContractEditor
                        value={newTopic.description}
                        onChange={(content) => setNewTopic({...newTopic, description: content})}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="topic-active"
                        checked={newTopic.is_active}
                        onChange={(e) => setNewTopic({...newTopic, is_active: e.target.checked})}
                        className="w-4 h-4"
                      />
                      <Label htmlFor="topic-active">Active (visible to clients)</Label>
                    </div>
                    <Button onClick={handleCreateTopic} disabled={loading} className="w-full">
                      {loading ? 'Creating...' : 'Create Topic'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {/* Topics List */}
            <div className="space-y-4">
              {supportiveTopics.map((topic) => (
                <Card key={topic.id}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <CardTitle className="flex items-center gap-2">
                          {topic.title}
                          <span className={`px-2 py-1 text-xs rounded ${topic.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                            {topic.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </CardTitle>
                        <CardDescription>
                          {topic.files?.length || 0} file(s) | Updated: {new Date(topic.updated_at).toLocaleDateString()}
                        </CardDescription>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setCurrentTopic(topic);
                            setShowUploadFiles(true);
                          }}
                        >
                          <Upload className="w-4 h-4 mr-1" />
                          Upload Files
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setCurrentTopic(topic);
                            setShowEditTopic(true);
                          }}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => {
                            setCurrentTopic(topic);
                            setShowDeleteTopic(true);
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div dangerouslySetInnerHTML={{ __html: topic.description }} className="prose max-w-none mb-4 text-sm" />
                    
                    {/* Files List */}
                    {topic.files && topic.files.length > 0 && (
                      <div className="mt-4">
                        <h4 className="text-sm font-semibold mb-2">Files:</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {topic.files.map((file) => (
                            <div key={file.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                              <div className="flex items-center gap-2">
                                <span>{getFileIcon(file.file_type)}</span>
                                <div>
                                  <p className="text-sm font-medium">{file.file_name}</p>
                                  <p className="text-xs text-gray-500">
                                    {(file.file_size / 1024 / 1024).toFixed(2)} MB
                                  </p>
                                </div>
                              </div>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleDeleteSupportiveFile(file.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}

              {supportiveTopics.length === 0 && (
                <Card className="bg-gray-50">
                  <CardContent className="text-center py-12">
                    <FolderOpen className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                    <p className="text-gray-600">No topics created yet</p>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Edit Topic Dialog */}
            <Dialog open={showEditTopic} onOpenChange={setShowEditTopic}>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Edit Topic</DialogTitle>
                </DialogHeader>
                {currentTopic && (
                  <div className="space-y-4">
                    <div>
                      <Label>Topic Title</Label>
                      <Input
                        value={currentTopic.title}
                        onChange={(e) => setCurrentTopic({...currentTopic, title: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <ContractEditor
                        value={currentTopic.description}
                        onChange={(content) => setCurrentTopic({...currentTopic, description: content})}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={currentTopic.is_active}
                        onChange={(e) => setCurrentTopic({...currentTopic, is_active: e.target.checked})}
                        className="w-4 h-4"
                      />
                      <Label>Active (visible to clients)</Label>
                    </div>
                    <Button onClick={handleUpdateTopic} disabled={loading} className="w-full">
                      {loading ? 'Updating...' : 'Update Topic'}
                    </Button>
                  </div>
                )}
              </DialogContent>
            </Dialog>

            {/* Delete Topic Dialog */}
            <Dialog open={showDeleteTopic} onOpenChange={setShowDeleteTopic}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Delete Topic</DialogTitle>
                  <DialogDescription>
                    Are you sure? This will delete the topic and all associated files permanently.
                  </DialogDescription>
                </DialogHeader>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setShowDeleteTopic(false)}>Cancel</Button>
                  <Button variant="destructive" onClick={handleDeleteTopic} disabled={loading}>
                    {loading ? 'Deleting...' : 'Delete'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Upload Files Dialog */}
            <Dialog open={showUploadFiles} onOpenChange={setShowUploadFiles}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Upload Files</DialogTitle>
                  <DialogDescription>
                    Upload up to 20 files (max 25MB each)
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Select Files</Label>
                    <Input
                      type="file"
                      multiple
                      onChange={(e) => setUploadingFiles(Array.from(e.target.files || []))}
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.zip,.jpg,.jpeg,.png"
                    />
                    {uploadingFiles.length > 0 && (
                      <p className="text-sm text-gray-600 mt-2">
                        {uploadingFiles.length} file(s) selected
                      </p>
                    )}
                  </div>
                  <Button onClick={handleUploadFiles} disabled={loading || uploadingFiles.length === 0} className="w-full">
                    {loading ? 'Uploading...' : 'Upload Files'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-4">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">Payment Settings</h2>
            
            {settings && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* LP Settings */}
                <Card className="bg-white border-gray-200">
                  <CardHeader>
                    <CardTitle>LP Company Settings</CardTitle>
                    <CardDescription>Payment details for LP clients</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="lp_iban">IBAN</Label>
                      <Input
                        id="lp_iban"
                        data-testid="lp-iban"
                        placeholder="GR00 0000 0000 0000 0000 0000 000"
                        value={settings.lp_iban || ''}
                        onChange={(e) => setSettings({ ...settings, lp_iban: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lp_iban_beneficiary">Beneficiary Name</Label>
                      <Input
                        id="lp_iban_beneficiary"
                        data-testid="lp-iban-beneficiary"
                        placeholder="Company or Person Name"
                        value={settings.lp_iban_beneficiary || ''}
                        onChange={(e) => setSettings({ ...settings, lp_iban_beneficiary: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lp_iban_bank_name">Bank Name</Label>
                      <Input
                        id="lp_iban_bank_name"
                        data-testid="lp-iban-bank-name"
                        placeholder="Bank Name"
                        value={settings.lp_iban_bank_name || ''}
                        onChange={(e) => setSettings({ ...settings, lp_iban_bank_name: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lp_iban_bic">BIC / SWIFT Code</Label>
                      <Input
                        id="lp_iban_bic"
                        data-testid="lp-iban-bic"
                        placeholder="AAAABBCCXXX"
                        value={settings.lp_iban_bic || ''}
                        onChange={(e) => setSettings({ ...settings, lp_iban_bic: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lp_revolut_tag">Revolut Tag</Label>
                      <Input
                        id="lp_revolut_tag"
                        data-testid="lp-revolut-tag"
                        placeholder="@deltakey"
                        value={settings.lp_revolut_tag || ''}
                        onChange={(e) => setSettings({ ...settings, lp_revolut_tag: e.target.value })}
                      />
                    </div>
                    <Button 
                      onClick={handleUpdateSettings} 
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      disabled={loading}
                    >
                      {loading ? 'Saving...' : 'Save LP Settings'}
                    </Button>
                  </CardContent>
                </Card>

                {/* LTD Settings */}
                <Card className="bg-white border-gray-200">
                  <CardHeader>
                    <CardTitle>LTD Company Settings</CardTitle>
                    <CardDescription>Payment details for LTD clients</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="ltd_stripe_link">Stripe Payment Link</Label>
                      <Input
                        id="ltd_stripe_link"
                        data-testid="ltd-stripe-link"
                        type="url"
                        placeholder="https://pay.stripe.com/..."
                        value={settings.ltd_stripe_link || ''}
                        onChange={(e) => setSettings({ ...settings, ltd_stripe_link: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ltd_iban">IBAN</Label>
                      <Input
                        id="ltd_iban"
                        data-testid="ltd-iban"
                        placeholder="GR00 0000 0000 0000 0000 0000 000"
                        value={settings.ltd_iban || ''}
                        onChange={(e) => setSettings({ ...settings, ltd_iban: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ltd_iban_beneficiary">Beneficiary Name</Label>
                      <Input
                        id="ltd_iban_beneficiary"
                        data-testid="ltd-iban-beneficiary"
                        placeholder="Company or Person Name"
                        value={settings.ltd_iban_beneficiary || ''}
                        onChange={(e) => setSettings({ ...settings, ltd_iban_beneficiary: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ltd_iban_bank_name">Bank Name</Label>
                      <Input
                        id="ltd_iban_bank_name"
                        data-testid="ltd-iban-bank-name"
                        placeholder="Bank Name"
                        value={settings.ltd_iban_bank_name || ''}
                        onChange={(e) => setSettings({ ...settings, ltd_iban_bank_name: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ltd_iban_bic">BIC / SWIFT Code</Label>
                      <Input
                        id="ltd_iban_bic"
                        data-testid="ltd-iban-bic"
                        placeholder="AAAABBCCXXX"
                        value={settings.ltd_iban_bic || ''}
                        onChange={(e) => setSettings({ ...settings, ltd_iban_bic: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ltd_wise_account">Wise Account</Label>
                      <Input
                        id="ltd_wise_account"
                        data-testid="ltd-wise-account"
                        placeholder="email@deltakey.com"
                        value={settings.ltd_wise_account || ''}
                        onChange={(e) => setSettings({ ...settings, ltd_wise_account: e.target.value })}
                      />
                    </div>
                    <Button 
                      onClick={handleUpdateSettings} 
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      disabled={loading}
                    >
                      {loading ? 'Saving...' : 'Save LTD Settings'}
                    </Button>
                  </CardContent>
                </Card>
              </div>
            )}
            
            {/* SMTP Configuration */}
            <div className="mt-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">SMTP Configuration</h2>
              <Card className="bg-white border-gray-200">
                <CardHeader>
                  <CardTitle>Email Server Settings</CardTitle>
                  <CardDescription>Configure SMTP settings for sending emails</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="smtp_host">SMTP Host</Label>
                      <Input
                        id="smtp_host"
                        placeholder="smtp.gmail.com"
                        value={smtpConfig.host}
                        onChange={(e) => setSmtpConfig({ ...smtpConfig, host: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="smtp_port">SMTP Port</Label>
                      <Input
                        id="smtp_port"
                        type="number"
                        placeholder="587"
                        value={smtpConfig.port}
                        onChange={(e) => setSmtpConfig({ ...smtpConfig, port: parseInt(e.target.value) })}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="smtp_username">Username</Label>
                    <Input
                      id="smtp_username"
                      placeholder="your-email@gmail.com"
                      value={smtpConfig.username}
                      onChange={(e) => setSmtpConfig({ ...smtpConfig, username: e.target.value })}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="smtp_password">Password</Label>
                    <Input
                      id="smtp_password"
                      type="password"
                      placeholder="Enter password"
                      value={smtpConfig.password}
                      onChange={(e) => setSmtpConfig({ ...smtpConfig, password: e.target.value })}
                    />
                    <p className="text-xs text-gray-500">Password is encrypted when saved</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="smtp_from_email">From Email</Label>
                    <Input
                      id="smtp_from_email"
                      type="email"
                      placeholder="noreply@deltakey.gr"
                      value={smtpConfig.from_email}
                      onChange={(e) => setSmtpConfig({ ...smtpConfig, from_email: e.target.value })}
                    />
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="smtp_use_tls"
                      checked={smtpConfig.use_tls}
                      onChange={(e) => setSmtpConfig({ ...smtpConfig, use_tls: e.target.checked })}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <Label htmlFor="smtp_use_tls" className="cursor-pointer">Use TLS (Recommended)</Label>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-3 pt-4">
                    <Button
                      onClick={handleTestSMTP}
                      disabled={testingSMTP || !smtpConfig.host || !smtpConfig.username || !smtpConfig.password}
                      className="bg-green-600 hover:bg-green-700 text-white flex-1"
                    >
                      {testingSMTP ? 'Testing...' : 'Test Connection'}
                    </Button>
                    <Button
                      onClick={handleSaveSMTPConfig}
                      disabled={savingSMTP || !smtpConfig.host || !smtpConfig.username || !smtpConfig.password}
                      className="bg-blue-600 hover:bg-blue-700 text-white flex-1"
                    >
                      {savingSMTP ? 'Saving...' : 'Save Configuration'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Income Tab */}
          <TabsContent value="income" className="space-y-4">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">Income Summary</h2>
            
            <Card className="bg-white border-gray-200">
              <CardHeader>
                <CardTitle>Filter Income</CardTitle>
                <CardDescription>Select year and optionally month to view income summary</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="income-year">Year</Label>
                    <Select
                      value={incomeYear.toString()}
                      onValueChange={(value) => setIncomeYear(parseInt(value))}
                    >
                      <SelectTrigger id="income-year">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[2025, 2024, 2023, 2022].map(year => (
                          <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="income-month">Month (Optional)</Label>
                    <Select
                      value={incomeMonth}
                      onValueChange={(value) => setIncomeMonth(value)}
                    >
                      <SelectTrigger id="income-month">
                        <SelectValue placeholder="All months" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">All months</SelectItem>
                        <SelectItem value="1">January</SelectItem>
                        <SelectItem value="2">February</SelectItem>
                        <SelectItem value="3">March</SelectItem>
                        <SelectItem value="4">April</SelectItem>
                        <SelectItem value="5">May</SelectItem>
                        <SelectItem value="6">June</SelectItem>
                        <SelectItem value="7">July</SelectItem>
                        <SelectItem value="8">August</SelectItem>
                        <SelectItem value="9">September</SelectItem>
                        <SelectItem value="10">October</SelectItem>
                        <SelectItem value="11">November</SelectItem>
                        <SelectItem value="12">December</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button onClick={fetchIncome} className="w-full bg-blue-600 hover:bg-blue-700">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Get Income Summary
                </Button>
              </CardContent>
            </Card>

            {incomeSummary && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* LTD Summary */}
                <Card className="bg-white border-gray-200">
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span>LTD Company</span>
                      <span className="text-sm font-normal text-gray-500">{incomeSummary.ltd_summary.file_count} files</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <div className="flex justify-between items-center gap-2">
                        <span className="text-sm text-gray-600 flex-shrink-0">Total Amount:</span>
                        <span className="text-base sm:text-xl font-bold text-gray-900 break-words text-right">€{incomeSummary.ltd_summary.total_amount.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between items-center gap-2">
                        <span className="text-sm text-green-600 flex-shrink-0">Paid:</span>
                        <span className="text-base sm:text-lg font-semibold text-green-600 break-words text-right">€{incomeSummary.ltd_summary.paid_amount.toFixed(2)}</span>
                      </div>
                      <div className="text-xs text-gray-500 text-right">{incomeSummary.ltd_summary.paid_count} files paid</div>
                      <div className="flex justify-between items-center gap-2">
                        <span className="text-sm text-red-600 flex-shrink-0">Unpaid:</span>
                        <span className="text-base sm:text-lg font-semibold text-red-600 break-words text-right">€{incomeSummary.ltd_summary.unpaid_amount.toFixed(2)}</span>
                      </div>
                      <div className="text-xs text-gray-500 text-right">{incomeSummary.ltd_summary.unpaid_count} files unpaid</div>
                    </div>
                  </CardContent>
                </Card>

                {/* LP Summary */}
                <Card className="bg-white border-gray-200">
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span>LP Company</span>
                      <span className="text-sm font-normal text-gray-500">{incomeSummary.lp_summary.file_count} files</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <div className="flex justify-between items-center gap-2">
                        <span className="text-sm text-gray-600 flex-shrink-0">Total Amount:</span>
                        <span className="text-base sm:text-xl font-bold text-gray-900 break-words text-right">€{incomeSummary.lp_summary.total_amount.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between items-center gap-2">
                        <span className="text-sm text-green-600 flex-shrink-0">Paid:</span>
                        <span className="text-base sm:text-lg font-semibold text-green-600 break-words text-right">€{incomeSummary.lp_summary.paid_amount.toFixed(2)}</span>
                      </div>
                      <div className="text-xs text-gray-500 text-right">{incomeSummary.lp_summary.paid_count} files paid</div>
                      <div className="flex justify-between items-center gap-2">
                        <span className="text-sm text-red-600 flex-shrink-0">Unpaid:</span>
                        <span className="text-base sm:text-lg font-semibold text-red-600 break-words text-right">€{incomeSummary.lp_summary.unpaid_amount.toFixed(2)}</span>
                      </div>
                      <div className="text-xs text-gray-500 text-right">{incomeSummary.lp_summary.unpaid_count} files unpaid</div>
                    </div>
                  </CardContent>
                </Card>

                {/* Total Summary */}
                <Card className="bg-blue-50 border-blue-200 md:col-span-2">
                  <CardHeader>
                    <CardTitle>Overall Total</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
                      <div className="py-2 sm:py-0">
                        <p className="text-sm text-gray-600 mb-1">Total</p>
                        <p className="text-xl sm:text-2xl font-bold text-gray-900 break-words">€{incomeSummary.total_summary.total_amount.toFixed(2)}</p>
                      </div>
                      <div className="py-2 sm:py-0">
                        <p className="text-sm text-green-600 mb-1">Paid</p>
                        <p className="text-xl sm:text-2xl font-bold text-green-600 break-words">€{incomeSummary.total_summary.paid_amount.toFixed(2)}</p>
                      </div>
                      <div className="py-2 sm:py-0">
                        <p className="text-sm text-red-600 mb-1">Unpaid</p>
                        <p className="text-xl sm:text-2xl font-bold text-red-600 break-words">€{incomeSummary.total_summary.unpaid_amount.toFixed(2)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminDashboard;
